import moment from 'moment';
import { all, cond, contains, equals, filter, not, pathOr, T } from 'ramda';

import { MOVE_OUT_PRORATE_MEHTODS } from '../containers/ResidentProfile/constants';
import { AFFORDABLE_PROGRAMS } from './affordable';

// Constants for automated transaction codes
const AUTOMATED_TRANSACTION_CODES_MVP = [
  'RENT',
  'SUBRENT',
  'RENTCRED',
  'SUBRENTCRED',
];

const AUTOMATED_TRANSACTION_CODES_V2 = [
  'UTILREIMB',
  'UTAC',
  'RDOVERAGE',
  'RDOVERAGECRED',
  'OVERAGE',
  'OVERAGECRED',
];

// Helper function to filter user-selectable monthly transaction codes
export const getSelectableTransactionCodes = (
  transactionCodes: Array<Object>,
  filterMlt = false,
) => {
  if (!transactionCodes) return [];

  return transactionCodes.filter((code) => {
    const isUserSelectable = code.userSelectable;
    const isMonthlyTransactionCode = pathOr(
      false,
      ['transactionCode', 'isMonthlyTransactionCode'],
      code,
    );

    return filterMlt
      ? isUserSelectable && isMonthlyTransactionCode
      : isUserSelectable;
  });
};

// Helper function to add disabled property to automated transaction codes when flags are enabled
export const addDisabledPropertyToAutomatedCodes = (
  codes: Array<Object>,
  isMvpEnabled: boolean,
  isV2Enabled: boolean,
  property: Object,
  hasRentChargesPermission: boolean,
  isCommercial: boolean = false,
  hasNonRentChargesPermission: boolean = false,
  automatedLeaseChargesFlagEnabled: boolean = false,
) => {
  return codes.map((code) => {
    const codeValue = code.transactionCode.code;
    const shouldDisableRentCode = disableRentCodes(
      codeValue,
      isMvpEnabled,
      isV2Enabled,
      property,
      hasRentChargesPermission,
      isCommercial,
    );
    const shouldDisableNonRentCode = disableNonRentCodes(
      codeValue,
      automatedLeaseChargesFlagEnabled,
      hasNonRentChargesPermission,
      isCommercial,
    );
    const isMonthlyTransactionCode =
      code.transactionCode.isMonthlyTransactionCode || false;

    return {
      ...code,
      disabled:
        shouldDisableRentCode ||
        (isMonthlyTransactionCode && shouldDisableNonRentCode),
    };
  });
};

// Helper function to determine if a specific rent code should be disabled
export const disableRentCodes = (
  code: string,
  isMvpEnabled: boolean,
  isV2Enabled: boolean,
  property: Object,
  hasRentChargesPermission: boolean,
  isCommercial: boolean = false,
): boolean => {
  // Do not disable if user has rent charges permission
  if (hasRentChargesPermission || isCommercial) {
    return false;
  }

  // Check if property has HUD or RD programs
  const propertyPrograms = pathOr([], ['pap'], property);
  const programNames = propertyPrograms.map((pap) =>
    pathOr('', ['masterAffordableProgram', 'name'], pap),
  );
  const hasHudOrRdPrograms =
    programNames.includes(AFFORDABLE_PROGRAMS.HUD) ||
    programNames.includes(AFFORDABLE_PROGRAMS.RD);

  const isMvpCode = AUTOMATED_TRANSACTION_CODES_MVP.includes(code);
  const isV2Code = AUTOMATED_TRANSACTION_CODES_V2.includes(code);

  // Only disable V1 (MVP) codes if the property does not have HUD or RD programs
  // or if the v2 flag is enabled and the property has RD or HUD
  const shouldDisableMvpCode =
    isMvpEnabled && isMvpCode && (!hasHudOrRdPrograms || isV2Enabled);
  const shouldDisableV2Code = isV2Enabled && isV2Code;

  return shouldDisableMvpCode || shouldDisableV2Code;
};

// Get selectable transaction codes based on feature flags
export const getFilteredTransactionCodes = (
  transactionCodes: Array<Object>,
  options: {
    isMvpEnabled: boolean,
    isV2Enabled: boolean,
    property: Object,
    hasRentChargesPermission: boolean,
    filterMLTCodes?: boolean,
    isCommercial?: boolean,
    hasNonRentChargesPermission: boolean,
    automatedLeaseChargesFlagEnabled: boolean,
  },
) => {
  const {
    isMvpEnabled,
    isV2Enabled,
    property,
    hasRentChargesPermission,
    filterMLTCodes = false,
    isCommercial = false,
    automatedLeaseChargesFlagEnabled = false,
    hasNonRentChargesPermission = false,
  } = options;

  const baseSelectableCodes = getSelectableTransactionCodes(
    transactionCodes,
    filterMLTCodes,
  );

  if (!isMvpEnabled && !isV2Enabled && !automatedLeaseChargesFlagEnabled) {
    return baseSelectableCodes;
  }

  return addDisabledPropertyToAutomatedCodes(
    baseSelectableCodes,
    isMvpEnabled,
    isV2Enabled,
    property,
    hasRentChargesPermission,
    isCommercial,
    hasNonRentChargesPermission,
    automatedLeaseChargesFlagEnabled,
  );
};

export const filterSelectableCodes = (txCodes: Array<Object>) =>
  filter((code) => code.userSelectable, txCodes);

export const filterOutApplicantOnlyCodes = (txCodes: Array<Object>) =>
  filter(
    (code) => !pathOr(false, ['transactionCode', 'isApplicantExcluded'], code),
    txCodes,
  );

export const filterOutAfterFASOnlyCodes = (txCodes: Array<Object>) =>
  filter(
    (code) => !pathOr(false, ['transactionCode', 'isAfterFASOnly'], code),
    txCodes,
  );

export const filterOutAfterFASExcludedCodes = (txCodes: Array<Object>) =>
  filter(
    (code) => !pathOr(false, ['transactionCode', 'isAfterFASExcluded'], code),
    txCodes,
  );

export const getCurrentResidentCodes = (txCodes: Array<Object>) =>
  filterSelectableCodes(filterOutAfterFASOnlyCodes(txCodes));

export const getApplicantCodes = (txCodes: Array<Object>) =>
  filterOutApplicantOnlyCodes(
    filterSelectableCodes(filterOutAfterFASOnlyCodes(txCodes)),
  );

export const getPriorResidentAfterFASCodes = (txCodes: Array<Object>) =>
  filterSelectableCodes(filterOutAfterFASExcludedCodes(txCodes));

export const transactionCodesProvider = (
  customerStatus: string,
  txCodes: Array<Object>,
  fasCreated: boolean = false,
) => {
  // $FlowFixMe
  const priorAfterFas = () => equals('Prior Resident') && fasCreated;
  const codeMap = cond([
    [equals('Applicant'), () => getApplicantCodes(txCodes)],
    [equals('Resident'), () => getCurrentResidentCodes(txCodes)],
    [priorAfterFas, () => getPriorResidentAfterFASCodes(txCodes)],
    [equals('Prior Resident'), () => getCurrentResidentCodes(txCodes)],
    [T, () => filterSelectableCodes(txCodes)],
  ]);

  return codeMap(customerStatus);
};

export const getTransactionIsReversible = (
  transaction: Object,
  ledgerIsOps: boolean = false,
  allowDomusoReversals: boolean = false,
  fasCreated: boolean = false,
  writtenOffBalance: number,
  propertyMoveOutProrateMethod: string,
  householdHasMoveOutProratedTransactions: boolean = false,
  moveOutProratedTransactionsDate,
) => {
  // $FlowFixMe
  const code = pathOr(
    '',
    ['propertyTransactionCode', 'transactionCode', 'code'],
    transaction,
  );
  // $FlowFixMe
  const transactionType = pathOr(
    '',
    ['propertyTransactionCode', 'transactionCode', 'transactionType', 'name'],
    transaction,
  );
  // $FlowFixMe
  const documentNumber = pathOr('', ['documentNumber'], transaction);

  const domusoReversalDisabled =
    !allowDomusoReversals &&
    ([
      'PYMTDOM',
      'FTPMTACH',
      'FTPMTCHECK',
      'FTPMTCHECKFH',
      'FTPMTCASH',
      'FTPMTDEPOSIT',
      'FTPMTCREDIT',
      'FTPMTDEBIT',
      'FTPMTMONEYORDER',
    ].includes(code) ||
      (code === 'PYMTPAYLS' &&
        documentNumber.indexOf('Rent, Order ID') !== -1));

  const transactionIsPosted = transaction.status === 'Posted';

  // $FlowFixMe
  const codeIsReversible = !pathOr(
    false,
    ['propertyTransactionCode', 'transactionCode', 'disableReversal'],
    transaction,
  );

  //TODO: Verify if this is equivalent to the next validation, if so, remove it.
  const isSubsidyReversible =
    !ledgerIsOps ||
    writtenOffBalance <= 0 ||
    pathOr(
      false,
      ['propertyTransactionCode', 'transactionCode', 'isPayment'],
      transaction,
    );

  const afterFASReversalDisabled =
    ledgerIsOps &&
    fasCreated &&
    contains(transactionType, ['Charge', 'Credit']);
  const afterFASReversalDisabledForSecurity =
    fasCreated && contains(transactionType, ['Security Charge']);

  const propertyMOPMIsStartOfMonthMOPM =
    propertyMoveOutProrateMethod === MOVE_OUT_PRORATE_MEHTODS.YES_MONTH_START;

  const transactionHasProrateCode =
    transaction.propertyTransactionCode.proRatePropertyTransactionCodeId !==
    null;

  const transactionWasPostedAlongsideProratedTransactions = moment(
    transaction.createdAt,
  ).isSame(moment(moveOutProratedTransactionsDate), 'day');

  const transactionIsMOProrated =
    transaction.isMoveOutProratedTxn ||
    (transactionHasProrateCode &&
      householdHasMoveOutProratedTransactions &&
      transactionWasPostedAlongsideProratedTransactions);

  return all(equals(true))([
    not(domusoReversalDisabled),
    not(afterFASReversalDisabled || afterFASReversalDisabledForSecurity),
    transactionIsPosted,
    codeIsReversible,
    isSubsidyReversible,
    not(propertyMOPMIsStartOfMonthMOPM && transactionIsMOProrated),
  ]);
};

export const disableNonRentCodes = (
  code: string,
  automatedLeaseChargesFlagEnabled: boolean = false,
  hasNonRentChargesPermission: boolean = false,
  isCommercial: boolean = false,
): boolean => {
  const isNonRentAutomatedCode = !AUTOMATED_TRANSACTION_CODES_MVP.concat(
    AUTOMATED_TRANSACTION_CODES_V2,
  ).includes(code);

  // Do not disable if user has non rent charges permission
  if (hasNonRentChargesPermission || isCommercial) {
    return false;
  }

  return automatedLeaseChargesFlagEnabled && isNonRentAutomatedCode;
};

export const isRentCode = (code: string): boolean => {
  return AUTOMATED_TRANSACTION_CODES_MVP.concat(
    AUTOMATED_TRANSACTION_CODES_V2,
  ).includes(code);
};
